import { lazy, Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/useAuth';
import { Toaster } from '@/components/ui/sonner';
import AppLayout from '@/components/layout/AppLayout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import ProtectedRoute from '@/features/auth/ProtectedRoute';
import PublicLayout from '@/features/marketing/PublicLayout';
import { useTrackPageView } from '@/lib/analytics/useTrackPageView';
import ConsentBanner from '@/components/ConsentBanner';

// ─── Routes EAGER (chargées dans le bundle initial) ─────────────────
// Ces pages sont vues en premier (login + dashboard public) et doivent
// rester instantanées au boot. Le AppLayout/Sidebar reste eager aussi
// pour ne pas flasher au passage en mode authentifié.
import Home from '@/pages/Home';
import Login from '@/pages/Login';
import PublicMenu from '@/pages/PublicMenu';
import PublicFood from '@/pages/PublicFood';

// Pages publiques additionnelles (lazy : pas critiques pour le boot)
const Events = lazy(() => import('@/pages/Events'));
const Reservations = lazy(() => import('@/pages/Reservations'));
const ReservationTable = lazy(() => import('@/pages/ReservationTable'));
const AdminEvents = lazy(() => import('@/pages/AdminEvents'));
const CustomerSignup = lazy(() => import('@/pages/CustomerSignup'));
const CustomerLogin = lazy(() => import('@/pages/CustomerLogin'));
const CustomerAccount = lazy(() => import('@/pages/CustomerAccount'));
const Unsubscribe = lazy(() => import('@/pages/Unsubscribe'));
const Remplacement = lazy(() => import('@/pages/Remplacement'));
const PublicCocktails = lazy(() => import('@/pages/PublicCocktails'));
const PublicTasting = lazy(() => import('@/pages/PublicTasting'));
const PublicAbout = lazy(() => import('@/pages/PublicAbout'));
const PublicJobs = lazy(() => import('@/pages/PublicJobs'));
const PublicKegs = lazy(() => import('@/pages/PublicKegs'));
const PublicJobDetail = lazy(() => import('@/pages/PublicJobDetail'));
const AdminJobs = lazy(() => import('@/pages/AdminJobs'));
const PolitiqueConfidentialite = lazy(
  () => import('@/pages/PolitiqueConfidentialite'),
);
const AdminTastingPublications = lazy(
  () => import('@/pages/AdminTastingPublications')
);
const AdminRatings = lazy(() => import('@/pages/AdminRatings'));
const AdminReservations = lazy(() => import('@/pages/AdminReservations'));
const AdminNewReservation = lazy(() => import('@/pages/AdminNewReservation'));
const AdminReservationFloorPlan = lazy(
  () => import('@/pages/AdminReservationFloorPlan')
);

// ─── Routes LAZY (chargées à la demande) ────────────────────────────
// Chaque page applicative devient un chunk séparé téléchargé seulement
// quand l'utilisateur navigue dessus. Réduction du bundle initial.
const Shifts = lazy(() => import('@/pages/Shifts'));
const ShiftDetail = lazy(() => import('@/pages/ShiftDetail'));
const Inventory = lazy(() => import('@/pages/Inventory'));
const InventoryListTab = lazy(
  () => import('@/features/inventory/tabs/InventoryListTab')
);
const InventoryOrdersTab = lazy(
  () => import('@/features/inventory/tabs/InventoryOrdersTab')
);
// ReceiveTab et HistoryTab sont des named exports du même fichier
// que InventoryOrdersTab — le browser ne télécharge le chunk qu'une
// fois, peu importe lequel des trois est rendu en premier.
const ReceiveTab = lazy(() =>
  import('@/features/inventory/tabs/InventoryOrdersTab').then((m) => ({
    default: m.ReceiveTab,
  }))
);
const HistoryTab = lazy(() =>
  import('@/features/inventory/tabs/InventoryOrdersTab').then((m) => ({
    default: m.HistoryTab,
  }))
);
const InventorySuppliersTab = lazy(
  () => import('@/features/inventory/tabs/InventorySuppliersTab')
);
const InventoryArchivesTab = lazy(
  () => import('@/features/inventory/tabs/InventoryArchivesTab')
);
const InventoryKegsTab = lazy(
  () => import('@/features/inventory/tabs/InventoryKegsTab')
);
const PayrollTips = lazy(() => import('@/pages/PayrollTips'));
const PayrollGridTab = lazy(
  () => import('@/features/payroll/tabs/PayrollGridTab')
);
const PayrollEmployeeTab = lazy(
  () => import('@/features/payroll/tabs/PayrollEmployeeTab')
);
const PayrollSummaryTab = lazy(
  () => import('@/features/payroll/tabs/PayrollSummaryTab')
);
const PayrollPayoutsTab = lazy(
  () => import('@/features/payroll/tabs/PayrollPayoutsTab')
);
const PayrollCorrectionsTab = lazy(
  () => import('@/features/payroll/tabs/PayrollCorrectionsTab')
);
const PayrollSalary = lazy(() => import('@/pages/PayrollSalary'));
const PayrollReport = lazy(() => import('@/pages/PayrollReport'));
const Employees = lazy(() => import('@/pages/Employees'));
const Keys = lazy(() => import('@/pages/Keys'));
const Directory = lazy(() => import('@/pages/Directory'));
const Roles = lazy(() => import('@/pages/Roles'));
const Settings = lazy(() => import('@/pages/Settings'));
const SettingsInventoryCategories = lazy(
  () => import('@/pages/SettingsInventoryCategories')
);
const SettingsSchedulePositions = lazy(
  () => import('@/pages/SettingsSchedulePositions')
);
const SettingsScheduleTemplates = lazy(
  () => import('@/pages/SettingsScheduleTemplates')
);
const Schedule = lazy(() => import('@/pages/Schedule'));
const ScheduleCalendarTab = lazy(
  () => import('@/features/schedule/tabs/ScheduleCalendarTab')
);
const ScheduleOpenShiftsTab = lazy(
  () => import('@/features/schedule/tabs/OpenShiftsTab')
);
const TeamScheduling = lazy(() => import('@/pages/TeamScheduling'));
const ScheduleSwapTab = lazy(
  () => import('@/features/schedule/tabs/SwapRequestsTab')
);
const ScheduleAvailabilityTab = lazy(
  () => import('@/features/schedule/tabs/AvailabilityTab')
);
// Vues employé (libre-service) réutilisées sous /horaire pour les employés
// avec schedule.view_own / schedule.availability (sans schedule.manage).
const MyScheduleSection = lazy(() =>
  import('@/features/schedule/MySchedulePanels').then((m) => ({
    default: m.MyScheduleSection,
  }))
);
const MyAvailabilitySection = lazy(() =>
  import('@/features/schedule/MySchedulePanels').then((m) => ({
    default: m.MyAvailabilitySection,
  }))
);
const TeamScheduleSection = lazy(
  () => import('@/features/schedule/TeamScheduleSection')
);
const FormationList = lazy(() => import('@/pages/FormationList'));
const FormationDetail = lazy(() => import('@/pages/FormationDetail'));
const FormationEmployee = lazy(() => import('@/pages/FormationEmployee'));
const MyProfile = lazy(() => import('@/pages/MyProfile'));
const HrDocumentEdit = lazy(() => import('@/pages/HrDocumentEdit'));
const HrDocumentFill = lazy(() => import('@/pages/HrDocumentFill'));
const MyDocuments = lazy(() => import('@/pages/MyDocuments'));
const EmployeeGuide = lazy(() => import('@/pages/EmployeeGuide'));
const BoutiqueEmployee = lazy(() => import('@/pages/BoutiqueEmployee'));
const SettingsBoutique = lazy(() => import('@/pages/SettingsBoutique'));
const SalesPrices = lazy(() => import('@/pages/SalesPrices'));
const WineSalesPrices = lazy(() => import('@/pages/WineSalesPrices'));
const KegSalePrices = lazy(() => import('@/pages/KegSalePrices'));
const CocktailsList = lazy(() => import('@/pages/CocktailsList'));
const CocktailDetail = lazy(() => import('@/pages/CocktailDetail'));
const FoodList = lazy(() => import('@/pages/FoodList'));
const FoodDetail = lazy(() => import('@/pages/FoodDetail'));
const AdminClusterImport = lazy(() => import('@/pages/AdminClusterImport'));
const AuditRapportsQuart = lazy(() => import('@/pages/AuditRapportsQuart'));
const SuiviCorrections = lazy(() => import('@/pages/SuiviCorrections'));
const SettingsCocktailAddons = lazy(
  () => import('@/pages/SettingsCocktailAddons')
);
const SettingsCocktailFormats = lazy(
  () => import('@/pages/SettingsCocktailFormats')
);
const SettingsOpeningHours = lazy(
  () => import('@/pages/SettingsOpeningHours')
);
const SettingsCustomers = lazy(() => import('@/pages/SettingsCustomers'));
const SettingsQRCodes = lazy(() => import('@/pages/SettingsQRCodes'));
const RecipesList = lazy(() => import('@/pages/RecipesList'));
const RecipesBouffe = lazy(() => import('@/pages/RecipesBouffe'));
const RecipeBouffeDetail = lazy(() => import('@/pages/RecipeBouffeDetail'));
const RecipeDetail = lazy(() => import('@/pages/RecipeDetail'));

/**
 * Fallback pendant le chargement d'un chunk de route — spinner doré
 * centré sur la zone de contenu. Hérite du fond Alambic via le thème
 * (bg-background du body).
 */
function PageLoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2
        className="h-8 w-8 text-primary animate-spin"
        aria-label="Chargement de la page…"
      />
    </div>
  );
}

// Index de /horaire : le planificateur (schedule.manage) voit le calendrier ;
// un employé (view_own/availability seulement) est redirigé vers sa vue.
function ScheduleIndex() {
  const { hasPermission } = useAuth();
  return hasPermission('schedule.manage') ? (
    <ScheduleCalendarTab />
  ) : (
    <Navigate to="/horaire/mon-horaire" replace />
  );
}

// Doit être à l'intérieur de BrowserRouter pour avoir useLocation —
// fire 'page_view' à chaque navigation SPA (GTM ne le voit pas sinon).
function PageViewTracker() {
  useTrackPageView();
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <PageViewTracker />
      <ErrorBoundary>
        <Suspense fallback={<PageLoadingFallback />}>
          <Routes>
            {/* ─── Site vitrine public (PublicLayout : navbar + footer marketing) ─── */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/menu" element={<PublicMenu />} />
              <Route path="/bouffe" element={<PublicFood />} />
              <Route path="/cocktails" element={<PublicCocktails />} />
              <Route path="/futs" element={<PublicKegs />} />
              <Route
                path="/degustation"
                element={<PublicTasting excludeWine />}
              />
              <Route
                path="/vins"
                element={
                  <PublicTasting
                    lockedType="wine"
                    pageTitle="Carte des vins — Bar L'Alambic"
                    pageDescription="Les vins disponibles au Bar L'Alambic à La Sarre. Prix bouteille et au verre, mis à jour à chaque inventaire."
                    heroTitle={
                      <>
                        Carte des <span className="italic text-[#D4A574]">vins</span>
                      </>
                    }
                  />
                }
              />
              <Route path="/evenements" element={<Events />} />
              <Route path="/emplois" element={<PublicJobs />} />
              <Route path="/emplois/:slug" element={<PublicJobDetail />} />
              <Route
                path="/politique-confidentialite"
                element={<PolitiqueConfidentialite />}
              />
              <Route path="/a-propos" element={<PublicAbout />} />
              <Route path="/reserver" element={<Reservations />} />
              <Route path="/reserver/table" element={<ReservationTable />} />
            </Route>

            {/* Page de connexion (publique, layout dédié) */}
            <Route path="/login" element={<Login />} />

            {/* Compte client (layout dédié, publique) */}
            <Route path="/compte/inscription" element={<CustomerSignup />} />
            <Route path="/compte/connexion" element={<CustomerLogin />} />
            <Route path="/mon-compte" element={<CustomerAccount />} />
            <Route path="/desabonnement" element={<Unsubscribe />} />

            {/* Acceptation d'un remplacement de quart (lien SMS, publique) */}
            <Route path="/remplacement/:token" element={<Remplacement />} />

            {/* ─── App de gestion (protégée) ─── */}
            <Route element={<ProtectedRoute />}>
              {/* Mon profil — accessible à tout employé authentifié,
                  SANS le sidebar admin (page autonome). */}
              <Route path="/mon-profil" element={<MyProfile />} />
              <Route
                path="/mon-profil/document/:id"
                element={<HrDocumentFill />}
              />

              <Route element={<AppLayout />}>
                {/* Sections principales */}
                <Route path="/shifts" element={<Shifts />} />
                <Route path="/shifts/:id" element={<ShiftDetail />} />
                <Route path="/inventory" element={<Inventory />}>
                  <Route index element={<InventoryListTab />} />
                  <Route path="orders" element={<InventoryOrdersTab />} />
                  <Route path="receive" element={<ReceiveTab />} />
                  <Route path="history" element={<HistoryTab />} />
                  <Route path="suppliers" element={<InventorySuppliersTab />} />
                  <Route path="archives" element={<InventoryArchivesTab />} />
                  <Route path="kegs" element={<InventoryKegsTab />} />
                </Route>
                <Route path="/payroll/tips" element={<PayrollTips />}>
                  <Route index element={<PayrollGridTab />} />
                  <Route path="employee" element={<PayrollEmployeeTab />} />
                  <Route path="summary" element={<PayrollSummaryTab />} />
                  <Route path="payouts" element={<PayrollPayoutsTab />} />
                  <Route path="corrections" element={<PayrollCorrectionsTab />} />
                </Route>
                <Route path="/payroll/salary" element={<PayrollSalary />} />
                <Route path="/payroll/report" element={<PayrollReport />} />
                <Route path="/employees" element={<Employees />} />
                <Route
                  path="/employees/documents/:id"
                  element={<HrDocumentEdit />}
                />
                <Route path="/keys" element={<Keys />} />
                <Route path="/repertoire" element={<Directory />} />
                <Route path="/admin/events" element={<AdminEvents />} />
                <Route path="/admin/emplois" element={<AdminJobs />} />
                <Route path="/admin/publications" element={<AdminTastingPublications />} />
                <Route path="/admin/avis" element={<AdminRatings />} />
                <Route path="/admin/reservations" element={<AdminReservations />} />
                <Route
                  path="/admin/reservations/nouvelle"
                  element={<AdminNewReservation />}
                />
                <Route
                  path="/admin/reservations/plan"
                  element={<AdminReservationFloorPlan />}
                />

                {/* Liste des prix de vente — accès owner/admin OU permission
                    prix-vente.* (gated dans chaque page via la page elle-même).
                    Exclut whisky/vins/canettes/fûts/cocktails qui ont leurs propres sous-onglets. */}
                <Route
                  path="/prix-vente"
                  element={
                    <SalesPrices
                      categoryFilter={(label) => {
                        const l = label.toLowerCase();
                        return (
                          !l.includes('whisky') &&
                          !l.startsWith('vin') &&
                          !l.includes('canette') &&
                          !l.includes('fût') &&
                          !l.includes('fut') &&
                          !l.includes('cocktail')
                        );
                      }}
                    />
                  }
                />
                <Route
                  path="/prix-vente/whisky"
                  element={
                    <SalesPrices
                      categoryFilter={(label) =>
                        label.toLowerCase().includes('whisky')
                      }
                      title="Liste des prix des whiskys"
                      requiredPermission="prix-vente.whisky"
                    />
                  }
                />
                <Route path="/prix-vente/vins" element={<WineSalesPrices />} />
                <Route
                  path="/prix-vente/canettes"
                  element={
                    <SalesPrices
                      categoryFilter={(label) =>
                        label.toLowerCase().includes('canette')
                      }
                      title="Liste des prix des canettes"
                      requiredPermission="prix-vente.canettes"
                    />
                  }
                />
                <Route path="/prix-vente/futs" element={<KegSalePrices />} />
                <Route path="/prix-vente/cocktails" element={<CocktailsList />} />
                <Route
                  path="/prix-vente/cocktails/:id"
                  element={<CocktailDetail />}
                />
                <Route path="/prix-vente/bouffe" element={<FoodList />} />
                <Route path="/prix-vente/bouffe/:id" element={<FoodDetail />} />
                <Route
                  path="/prix-vente/extras"
                  element={
                    <FoodList
                      lockedCategoryLabel="Extras"
                      pageTitle="Extras (add-ons)"
                    />
                  }
                />
                <Route
                  path="/prix-vente/preparations"
                  element={
                    <FoodList
                      lockedCategoryLabel="Préparations"
                      pageTitle="Préparations (interne staff)"
                    />
                  }
                />
                <Route path="/admin/cluster-import" element={<AdminClusterImport />} />
                <Route path="/admin/audit-rapports" element={<AuditRapportsQuart />} />
                <Route path="/admin/suivi-corrections" element={<SuiviCorrections />} />

                {/* Horaire des quarts : calendrier + à combler + disponibilités */}
                <Route path="/horaire" element={<Schedule />}>
                  <Route index element={<ScheduleIndex />} />
                  {/* Vues employé (libre-service) */}
                  <Route path="mon-horaire" element={<MyScheduleSection />} />
                  <Route
                    path="horaire-equipe"
                    element={<TeamScheduleSection />}
                  />
                  <Route
                    path="mes-disponibilites"
                    element={<MyAvailabilitySection />}
                  />
                  {/* Vues planificateur */}
                  <Route path="a-combler" element={<ScheduleOpenShiftsTab />} />
                  <Route
                    path="disponibilites"
                    element={<ScheduleAvailabilityTab />}
                  />
                  <Route path="equipe" element={<TeamScheduling />} />
                  <Route path="echanges" element={<ScheduleSwapTab />} />
                </Route>

                {/* Formation : liste + détail (formateur) + vue employé */}
                <Route path="/formation" element={<FormationList />} />
                <Route path="/formation/:id" element={<FormationDetail />} />
                <Route path="/ma-formation" element={<FormationEmployee />} />

                {/* Mes documents — vue employé (avec sidebar) */}
                <Route path="/mes-documents" element={<MyDocuments />} />

                {/* Guide d'utilisation — vue employé */}
                <Route path="/aide" element={<EmployeeGuide />} />

                {/* Boutique de linge à points — vue employé */}
                <Route path="/boutique" element={<BoutiqueEmployee />} />

                {/* Mode Service — recettes consultables par tous, sans prix */}
                <Route path="/recettes" element={<RecipesList />} />
                <Route path="/recettes/bouffe" element={<RecipesBouffe />} />
                <Route path="/recettes/bouffe/:id" element={<RecipeBouffeDetail />} />
                <Route path="/recettes/:id" element={<RecipeDetail />} />

                {/* Paramètres : layout 2 colonnes (sub-sidebar + Outlet) */}
                <Route path="/settings" element={<Settings />}>
                  <Route index element={<Navigate to="/settings/roles" replace />} />
                  <Route path="roles" element={<Roles />} />
                  <Route
                    path="inventory/categories"
                    element={<SettingsInventoryCategories />}
                  />
                  <Route
                    path="cocktails/addons"
                    element={<SettingsCocktailAddons />}
                  />
                  <Route
                    path="cocktails/formats"
                    element={<SettingsCocktailFormats />}
                  />
                  <Route
                    path="opening-hours"
                    element={<SettingsOpeningHours />}
                  />
                  <Route path="boutique" element={<SettingsBoutique />} />
                  <Route path="customers" element={<SettingsCustomers />} />
                  <Route path="qr-codes" element={<SettingsQRCodes />} />
                  <Route
                    path="schedule/positions"
                    element={<SettingsSchedulePositions />}
                  />
                  <Route
                    path="schedule/templates"
                    element={<SettingsScheduleTemplates />}
                  />
                </Route>

                {/* Compat : ancienne URL /roles redirige vers /settings/roles */}
                <Route path="/roles" element={<Navigate to="/settings/roles" replace />} />
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </ErrorBoundary>

      {/* Bandeau consent Loi 25 (QC) — affiché tant que pas de choix.
          Mount au-dessus du Toaster pour rester visible si toast ouvert. */}
      <ConsentBanner />

      {/* Toaster global — affiche les notifications partout */}
      <Toaster
        position="top-right"
        theme="dark"
        richColors
        closeButton
      />
    </BrowserRouter>
  );
}

export default App;
